import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache SEO content per lang so we don't refetch on every route change
const seoCache = {};

async function loadSeo(lang) {
  if (seoCache[lang]) return seoCache[lang];
  try {
    const r = await axios.get(`${API}/cms/content`, { params: { page: "seo", lang } });
    seoCache[lang] = r.data.content || {};
    return seoCache[lang];
  } catch {
    return {};
  }
}

function setMeta(selector, attr, value) {
  const el = document.querySelector(selector);
  if (el && value !== undefined && value !== null && value !== "") {
    el.setAttribute(attr, value);
  }
}

function setJsonLd(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

/**
 * SEOHead — props: `page` (e.g. "home"), optional title/description overrides.
 * Imperatively updates <title>, meta tags, hreflangs, canonical, JSON-LD
 * every time page or lang changes. Works without react-helmet.
 */
export default function SEOHead({ page }) {
  const { lang } = useLang();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const seo = await loadSeo(lang);
      if (!mounted) return;

      const pick = (k, fallback = "") => seo[`seo.${k}`]?.value || fallback;
      const site = pick("site.name", "Dosmass");
      const title = pick(`${page}.title`, site);
      const description = pick(`${page}.description`, "");
      const keywords = pick(`${page}.keywords`, "");
      const ogImageRaw = pick("site.default_og_image", "/og-image.png");
      const twitter = pick("site.twitter_handle", "");

      // Resolve absolute URLs
      const origin = window.location.origin;
      const pageUrl = origin + location.pathname;
      const ogImage = ogImageRaw.startsWith("http")
        ? ogImageRaw
        : origin + ogImageRaw;

      // <html lang="...">
      document.documentElement.setAttribute("lang", lang);

      // Title
      document.title = title;

      // Primary meta
      setMeta('meta[name="description"]', "content", description);
      // keywords (optional, retained for legacy crawlers)
      let kw = document.querySelector('meta[name="keywords"]');
      if (!kw) {
        kw = document.createElement("meta");
        kw.setAttribute("name", "keywords");
        document.head.appendChild(kw);
      }
      kw.setAttribute("content", keywords);

      // Canonical
      setMeta("#seo-canonical", "href", pageUrl);

      // Open Graph
      setMeta("#seo-og-title", "content", title);
      setMeta("#seo-og-desc", "content", description);
      setMeta("#seo-og-url", "content", pageUrl);
      setMeta("#seo-og-image", "content", ogImage);
      setMeta("#seo-og-site", "content", site);
      setMeta("#seo-og-locale", "content", lang === "es" ? "es_CO" : "en_US");

      // Twitter
      setMeta("#seo-tw-title", "content", title);
      setMeta("#seo-tw-desc", "content", description);
      setMeta("#seo-tw-image", "content", ogImage);
      if (twitter) setMeta("#seo-tw-site", "content", twitter);

      // hreflang — each language gets its own canonical URL (?lang=…)
      // so Google can index ES + EN as separate pages.
      const buildLangUrl = (l) => {
        const u = new URL(pageUrl);
        u.searchParams.set("lang", l);
        return u.toString();
      };
      setMeta("#seo-hreflang-es", "href", buildLangUrl("es"));
      setMeta("#seo-hreflang-en", "href", buildLangUrl("en"));
      setMeta("#seo-hreflang-default", "href", buildLangUrl("es"));

      const inLang = lang === "es" ? "es-CO" : "en-US";
      const orgId = origin + "/#organization";
      const siteId = origin + "/#website";
      const logoUrl = origin + "/webflow/img/670702412be0c029c134d216_logo.svg";
      const contactEmail = pick("site.contact_email", "info@dosmass.com");
      const sameAs = [
        "https://www.facebook.com/",
        "https://www.instagram.com/",
        "https://twitter.com/",
      ];

      // Organization JSON-LD (site-wide — publisher identity)
      setJsonLd("seo-jsonld-organization", {
        "@context": "https://schema.org",
        "@type": "Organization",
        "@id": orgId,
        name: site,
        url: origin,
        logo: {
          "@type": "ImageObject",
          url: logoUrl,
        },
        sameAs,
        contactPoint: [
          {
            "@type": "ContactPoint",
            contactType: "customer service",
            email: contactEmail,
            availableLanguage: ["Spanish", "English"],
          },
        ],
      });

      // WebSite JSON-LD (with publisher ref + inLanguage)
      setJsonLd("seo-jsonld-website", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": siteId,
        name: site,
        url: origin,
        inLanguage: inLang,
        publisher: { "@id": orgId },
      });

      // Determine page-specific @type
      const pageTypeMap = {
        home: "WebPage",
        about: "AboutPage",
        contact: "ContactPage",
        services: "CollectionPage",
        demos: "CollectionPage",
        terms: "WebPage",
        privacy: "WebPage",
      };
      const webPageType = pageTypeMap[page] || "WebPage";

      // WebPage JSON-LD for the current route
      setJsonLd("seo-jsonld-webpage", {
        "@context": "https://schema.org",
        "@type": webPageType,
        name: title,
        description,
        url: pageUrl,
        inLanguage: inLang,
        isPartOf: { "@id": siteId },
        about: { "@id": orgId },
      });

      // Breadcrumbs JSON-LD
      const crumbs = [
        { "@type": "ListItem", position: 1, name: site, item: origin },
      ];
      if (location.pathname !== "/") {
        const segs = location.pathname.split("/").filter(Boolean);
        segs.forEach((seg, i) => {
          crumbs.push({
            "@type": "ListItem",
            position: i + 2,
            name: pick(`${seg}.title`, seg.replace(/-/g, " ")),
            item: origin + "/" + segs.slice(0, i + 1).join("/"),
          });
        });
      }
      setJsonLd("seo-jsonld-breadcrumb", {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: crumbs,
      });

      // Page-specific extra schemas
      const extraId = "seo-jsonld-page-extra";
      if (page === "services") {
        // Service catalog on /services index
        const services = [
          { slug: "sales", name: lang === "es" ? "Ventas Conversacionales" : "Conversational Sales" },
          { slug: "logistics", name: lang === "es" ? "Logística y Operaciones" : "Logistics & Operations" },
          { slug: "data-analysis", name: lang === "es" ? "Análisis de Datos" : "Data Analysis" },
          { slug: "support", name: lang === "es" ? "Atención al Cliente" : "Customer Support" },
          { slug: "credit", name: lang === "es" ? "Crédito y Originación" : "Credit & Origination" },
        ];
        setJsonLd(extraId, {
          "@context": "https://schema.org",
          "@type": "OfferCatalog",
          name: title,
          itemListElement: services.map((s, i) => ({
            "@type": "Offer",
            position: i + 1,
            itemOffered: {
              "@type": "Service",
              name: s.name,
              provider: { "@id": orgId },
              url: origin + "/services/" + s.slug,
              areaServed: ["CO", "LATAM", "US"],
            },
          })),
        });
      } else if (page === "contact") {
        // ContactPoint emphasised on contact page
        setJsonLd(extraId, {
          "@context": "https://schema.org",
          "@type": "ContactPoint",
          contactType: "customer service",
          email: contactEmail,
          availableLanguage: ["Spanish", "English"],
          areaServed: ["CO", "LATAM", "US"],
        });
      } else {
        // Remove any stale extra schema from a previous page
        const stale = document.getElementById(extraId);
        if (stale) stale.remove();
      }
    })();
    return () => { mounted = false; };
  }, [page, lang, location.pathname]);

  return null;
}
